import React, { useState } from 'react'

import {
  Box,
  CellRenderer,
  CellRenderers,
  Chip,
  CircularProgress,
  Column,
  ConfirmDialog,
  RoundedPlainTextButton,
} from '@percept/mui'

import { Check, Close, Edit } from '@percept/mui/icons'

import { MediaAcademyAdminTable, MediaAcademyAdminTableProps } from './MediaAcademyAdminTable'

import { getPaginatedResources, useDeleteMediaAcademyResource } from '../hooks'

import { mediaAcademyResouceCategoryLabels } from '../lib'

import { formatUTC } from '@percept/utils'

import { InfinitePaginationWrapper } from '@percept/types'

import { MediaAcademyResource } from '../typings'


type MediaAcademyResourceTableRow = MediaAcademyResource & {
  actions?: null
}

const columns: Column<MediaAcademyResourceTableRow>[] = [
  {
    key: 'name',
    label: 'Name',
    align: 'left',
  },
  {
    key: 'category',
    label: 'Category',
    align: 'left',
  },
  {
    key: 'published_date',
    label: 'Publication Date',
    align: 'left',
  },
  {
    key: 'actions',
    label: 'Actions',
    align: 'right',
  },
]

export type ResourceAdminTableProps = (
  Omit<
    MediaAcademyAdminTableProps<MediaAcademyResourceTableRow>,
    | 'name'
    | 'queryFn'
    | 'columns'
    | 'renderers'
  > & {
    setEditResource: (resource: MediaAcademyResource) => void
    invalidateQuery: () => void
  }
)

const renderers: CellRenderers<MediaAcademyResourceTableRow> = {
  category: ({ category }) => <>
    <Chip
      color='secondary'
      size='small'
      variant='outlined'
      label={mediaAcademyResouceCategoryLabels[category]} />
  </>,
  published_date: ({ published_date }) => <>
    { formatUTC(new Date(published_date), 'dd/MM/yy') }
  </>
}

const queryFn = (page: number): Promise<InfinitePaginationWrapper<MediaAcademyResourceTableRow>> => (
  getPaginatedResources({
    order: 'DESC',
    page,
    limit: 10,
  })
)

export const ResourceAdminTable = ({
  setEditResource,
  invalidateQuery,
  ...props
}: ResourceAdminTableProps): JSX.Element => {

  const [resourceToDelete, setResourceToDelete] = useState<MediaAcademyResource | null>(null)

  const deletionQuery = useDeleteMediaAcademyResource({
    onSuccess: () => {
      invalidateQuery()
      setResourceToDelete(null)
    },
  })

  const actionsRenderer: CellRenderer<MediaAcademyResourceTableRow> = (resource): JSX.Element => {
    return (
      <Box display='flex' alignItems='center' justifyContent='flex-end'>
        <Box mr={3}>
          <RoundedPlainTextButton
            startIcon={<Edit />}
            size='small'
            variant='contained'
            onClick={(): void => {
              setEditResource(resource)
            }}>
            Edit
          </RoundedPlainTextButton>
        </Box>
        <RoundedPlainTextButton
          color='secondary'
          startIcon={<Close />}
          size='small'
          variant='contained'
          onClick={(): void => {
            setResourceToDelete(resource)
          }}>
          Delete
        </RoundedPlainTextButton>
      </Box>
    )
  }

  return (
    <>
      <MediaAcademyAdminTable
        name='resources'
        queryFn={queryFn}
        columns={columns}
        renderers={{
          ...renderers,
          actions: actionsRenderer,
        }}
        {...props} />
      
      { resourceToDelete && (
        <ConfirmDialog
          title='Delete Resource'
          confirmText='Delete'
          confirmDisabled={deletionQuery.isLoading}
          confirmIcon={
            deletionQuery.isLoading ?
              <CircularProgress size='1em' color='inherit' /> :
              <Check />
          }
          content={
            <Box fontSize={14}>
              The resource &apos;{resourceToDelete.name}&apos; will be deleted.
              <p><strong>This action cannot be undone.</strong></p>
            </Box>
          }
          onConfirm={(): void => {
            if( resourceToDelete ){
              deletionQuery.mutate({ id: resourceToDelete.id })
            }
          }}
          onCancel={(): void => setResourceToDelete(null)} />
      )}
    </>
  )
}
